import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MEDIA_CONFIG } from '../data/media';
import { motion, AnimatePresence } from 'framer-motion';
import VideoPlayer from '../components/VideoPlayer';
import { useVideoPreloader } from '../hooks/useVideoPreloader';

function Portfolio() {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoKey, setVideoKey] = useState(0); // Force remounting of video player
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);

  const currentVideo = MEDIA_CONFIG.gallery.videos[currentVideoIndex];
  const totalVideos = MEDIA_CONFIG.gallery.videos.length;
  
  // Use video preloader hook
  const { isLoading: isVideoLoading, hasError: hasVideoError, getError } = useVideoPreloader(
    MEDIA_CONFIG.gallery.videos,
    currentVideoIndex,
    { preloadNext: true, preloadPrevious: true, maxCacheSize: 5 }
  );
  
  // Create an extended array for infinite scrolling
  const extendedVideos = [
    ...MEDIA_CONFIG.gallery.videos,
    ...MEDIA_CONFIG.gallery.videos,
    ...MEDIA_CONFIG.gallery.videos
  ];
  
  // Thumbnail dimensions
  const thumbnailWidth = 192; // width of each thumbnail (48px * 4)
  const thumbnailGap = 16; // gap between thumbnails
  const thumbnailTotalWidth = thumbnailWidth + thumbnailGap;

  // Calculate visible thumbnails on mount and resize
  useEffect(() => {
    const calculateVisibleThumbnails = () => {
      if (sliderRef.current) {
        const containerWidth = sliderRef.current.clientWidth - 64; // Subtract padding
        setSliderWidth(containerWidth);
        
        // Initialize offset to center the first video
        const visibleCount = Math.floor(containerWidth / thumbnailTotalWidth);
        const middlePosition = Math.floor(visibleCount / 2);
        const offsetIndex = currentVideoIndex + totalVideos; // Use the middle set
        const idealOffset = (offsetIndex - middlePosition) * thumbnailTotalWidth;
        setCurrentOffset(idealOffset);
      }
    };

    calculateVisibleThumbnails();
    
    const resizeObserver = new ResizeObserver(calculateVisibleThumbnails);
    if (sliderRef.current) {
      resizeObserver.observe(sliderRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [currentVideoIndex, totalVideos]);

  // Update offset when current index changes
  useEffect(() => {
    if (sliderWidth > 0) {
      const visibleCount = Math.floor(sliderWidth / thumbnailTotalWidth);
      const middlePosition = Math.floor(visibleCount / 2);
      const offsetIndex = currentVideoIndex + totalVideos; // Use the middle set
      const idealOffset = (offsetIndex - middlePosition) * thumbnailTotalWidth;
      
      setCurrentOffset(idealOffset);
    }
  }, [currentVideoIndex, sliderWidth, totalVideos]);

  const handleVideoChange = useCallback((index: number) => {
    // Convert the extended index to a real index (0 to totalVideos-1)
    const realIndex = ((index % totalVideos) + totalVideos) % totalVideos;
    
    if (realIndex === currentVideoIndex) return;
    
    // Force video player remount by changing key
    setVideoKey(prev => prev + 1);
    setIsPlaying(false);
    setCurrentVideoIndex(realIndex);
  }, [currentVideoIndex, totalVideos]);

  const handlePrevVideo = useCallback(() => {
    const newIndex = currentVideoIndex === 0 ? totalVideos - 1 : currentVideoIndex - 1;
    handleVideoChange(newIndex);
  }, [currentVideoIndex, totalVideos, handleVideoChange]);

  const handleNextVideo = useCallback(() => {
    const newIndex = currentVideoIndex === totalVideos - 1 ? 0 : currentVideoIndex + 1;
    handleVideoChange(newIndex);
  }, [currentVideoIndex, totalVideos, handleVideoChange]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextVideo();
    }

    if (isRightSwipe) {
      handlePrevVideo();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return; // Don't interfere with form inputs
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevVideo();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNextVideo();
          break;
        case ' ':
          e.preventDefault();
          // Space bar handled by video player
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevVideo, handleNextVideo]);

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <div className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <img
          src={`${MEDIA_CONFIG.paths.backgrounds}/headers/portfolio-header.jpg`}
          alt="Portfolio Header"
          className="absolute w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Portfolio</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Ausgewählte Arbeiten und Projekte
          </p>
        </div>
      </div>

      {/* Video Gallery */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="relative aspect-video max-w-7xl mx-auto mb-8">
          <VideoPlayer
            key={`video-${currentVideoIndex}-${videoKey}`} // Force remount on video change
            src={currentVideo.videoUrl}
            poster={currentVideo.thumbnailUrl}
            showFullscreen={true}
            showQualitySelector={true}
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
            showTitle={false}
          />
        </div>

        {/* Projekt-Details */}
        <motion.div 
          key={`details-${currentVideoIndex}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-zinc-900 p-6 rounded-lg mb-12"
        >
          <h3 className="text-xl font-semibold mb-2">{currentVideo.title}</h3>
          <p className="text-gray-300 mb-6">{currentVideo.description}</p>
          
          <h4 className="text-lg font-semibold mb-4">Projekt Credits</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-gray-400">Regie</p>
              <p className="font-medium">{currentVideo.credits.director}</p>
            </div>
            <div>
              <p className="text-gray-400">Kamera</p>
              <p className="font-medium">{currentVideo.credits.cinematographer}</p>
            </div>
            <div>
              <p className="text-gray-400">Produktion</p>
              <p className="font-medium">{currentVideo.credits.production}</p>
            </div>
            <div>
              <p className="text-gray-400">Schnitt</p>
              <p className="font-medium">{currentVideo.credits.editor}</p>
            </div>
          </div>
        </motion.div>

        {/* Thumbnail Navigation - Infinite Carousel */}
        <div className="relative mb-8">
          <button
            onClick={handlePrevVideo}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
            aria-label="Vorheriges Video"
          >
            <ChevronLeft size={24} />
          </button>
          
          <button
            onClick={handleNextVideo}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
            aria-label="Nächstes Video"
          >
            <ChevronRight size={24} />
          </button>
          
          <div 
            ref={sliderRef}
            className="overflow-hidden px-12"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              ref={thumbnailsContainerRef}
              className="flex transition-transform duration-300 ease-out"
              style={{ 
                transform: `translateX(${-currentOffset}px)`,
                gap: `${thumbnailGap}px`
              }}
            >
              {extendedVideos.map((video, index) => {
                // Calculate the real index for the current position in the extended array
                const realIndex = index % totalVideos;
                
                return (
                  <div 
                    key={`thumbnail-${index}`}
                    className={`flex-shrink-0 w-48 cursor-pointer transition-all duration-200 ${
                      currentVideoIndex === realIndex ? 'scale-105 ring-2 ring-white' : 'opacity-70 hover:opacity-100'
                    }`}
                    onClick={() => handleVideoChange(index)}
                  >
                    <img 
                      src={video.thumbnailUrl} 
                      alt={video.title}
                      className="w-full aspect-video object-cover rounded"
                      loading="lazy"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pagination Indicator */}
        <div className="flex justify-center items-center space-x-2">
          {Array.from({ length: totalVideos }).map((_, index) => (
            <button
              key={`indicator-${index}`}
              onClick={() => handleVideoChange(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                currentVideoIndex === index 
                  ? 'bg-white w-4' 
                  : 'bg-gray-600 hover:bg-gray-400'
              }`}
              aria-label={`Gehe zu Video ${index + 1}`}
            />
          ))}
        </div>

        {/* Navigation hints */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Verwenden Sie die Pfeiltasten ← → zur Navigation zwischen Videos</p>
        </div>
      </div>
    </div>
  );
}

export default Portfolio;